import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import {
  useSchedules,
  useCreateSchedule,
  useCloseSchedule,
  useDeleteSchedule,
} from "@/application/hooks/useSchedules";
import type { SchedulePanelContainerProps } from "./types";
import { SchedulePanel } from "./SchedulePanel";

/**
 * SchedulePanel container component.
 * Handles data fetching, mutations, and state management.
 * Passes processed data to the SchedulePanel presenter.
 */
export function SchedulePanelContainer({ tripId }: SchedulePanelContainerProps) {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDateOptions, setNewDateOptions] = useState<string[]>([]);
  const [dateInput, setDateInput] = useState("");
  const [expandedSchedules, setExpandedSchedules] = useState<Set<string>>(new Set());

  const { data: schedules = [], isLoading } = useSchedules(tripId);
  const createMutation = useCreateSchedule(tripId);
  const closeMutation = useCloseSchedule(tripId);
  const deleteMutation = useDeleteSchedule(tripId);

  const handleAddDate = () => {
    if (dateInput && !newDateOptions.includes(dateInput)) {
      setNewDateOptions([...newDateOptions, dateInput].sort());
      setDateInput("");
    }
  };

  const handleRemoveDate = (date: string) => {
    setNewDateOptions(newDateOptions.filter((d) => d !== date));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || newDateOptions.length < 1) return;

    try {
      await createMutation.mutateAsync({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        dateOptions: newDateOptions,
      });
      setIsCreating(false);
      setNewTitle("");
      setNewDescription("");
      setNewDateOptions([]);
      toast.success(t("schedules.createSuccess", "日程調整を作成しました"));
    } catch {
      toast.error(t("schedules.createError", "日程調整の作成に失敗しました"));
    }
  };

  const handleClose = async (scheduleId: string) => {
    try {
      await closeMutation.mutateAsync(scheduleId);
      toast.success(t("schedules.closeSuccess", "日程調整を確定しました"));
    } catch {
      toast.error(t("schedules.closeError", "日程調整の確定に失敗しました"));
    }
  };

  const handleDelete = async (scheduleId: string) => {
    const confirmed = await confirm({
      title: t("schedules.deleteSchedule"),
      description: t("schedules.confirmDelete"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(scheduleId);
      toast.success(t("schedules.deleteSuccess", "日程調整を削除しました"));
    } catch {
      toast.error(t("schedules.deleteError", "日程調整の削除に失敗しました"));
    }
  };

  const toggleScheduleExpand = (scheduleId: string) => {
    setExpandedSchedules((prev) => {
      const next = new Set(prev);
      if (next.has(scheduleId)) {
        next.delete(scheduleId);
      } else {
        next.add(scheduleId);
      }
      return next;
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTitle("");
    setNewDescription("");
    setNewDateOptions([]);
  };

  return (
    <SchedulePanel
      tripId={tripId}
      schedules={schedules}
      isLoading={isLoading}
      isCreating={isCreating}
      newTitle={newTitle}
      newDescription={newDescription}
      newDateOptions={newDateOptions}
      dateInput={dateInput}
      expandedSchedules={expandedSchedules}
      isCreatingSchedule={createMutation.isPending}
      isClosing={closeMutation.isPending}
      isDeleting={deleteMutation.isPending}
      onNewTitleChange={setNewTitle}
      onNewDescriptionChange={setNewDescription}
      onDateInputChange={setDateInput}
      onAddDate={handleAddDate}
      onRemoveDate={handleRemoveDate}
      onCreate={handleCreate}
      onCancelCreate={handleCancelCreate}
      onStartCreate={() => setIsCreating(true)}
      onClose={handleClose}
      onDelete={handleDelete}
      onToggleExpand={toggleScheduleExpand}
      dialogProps={dialogProps}
    />
  );
}
